import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GenericModal } from "@/components/GenericModal/GenericModal";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { ErrorReason, WrongAmountComparison } from "@/types/Flashscan";
import { resolveSaleManually } from "@/api/sales";
import { resolveBillManually } from "@/api/bills";

import "./FlashscanManualResolutionModal.scss";

export type FlashscanManualResolutionTarget = {
  id: string;
  errorReason: ErrorReason;
  title: string;
  providerId: string;
  impactAmount: number;
  additionalInfos?: string;
  wrongAmountComparison?: WrongAmountComparison;
  entityType: "SALE" | "BILL";
};

export const FlashscanManualResolutionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  target: FlashscanManualResolutionTarget | null;
  onResolved: () => Promise<void> | void;
}> = ({ isOpen, onClose, target, onResolved }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const title = useMemo(() => {
    if (!target) return t("pages.flashscan.manualResolution.titleFallback");
    return t("pages.flashscan.manualResolution.title", {
      rule: t(`pages.flashscan.rules.${target.errorReason}`),
    });
  }, [t, target]);

  const close = useCallback(() => {
    setComment("");
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  const apply = useCallback(
    async (action: "IGNORE" | "VALIDATE_MANUALLY") => {
      if (!target) return;
      setIsSubmitting(true);
      try {
        const payload = { action, userComment: comment.trim() || null };
        if (target.entityType === "SALE") {
          await resolveSaleManually(target.id, payload);
        } else {
          await resolveBillManually(target.id, payload);
        }
        await onResolved();
        close();
      } finally {
        setIsSubmitting(false);
      }
    },
    [close, comment, onResolved, target],
  );

  const isWrongAmount = target?.errorReason === "WRONG_AMOUNT";
  const comparison = target?.wrongAmountComparison;
  const primaryBill = comparison?.bills[0] ?? null;
  const billsTotal = comparison?.billsTotalAmountDue ?? null;
  const saleAmount = comparison?.sale.amount ?? null;
  const saleDateLabel = useMemo(() => {
    const raw = comparison?.sale.closeDate;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("fr-FR");
  }, [comparison?.sale.closeDate]);
  const billPeriodLabel = useMemo(() => {
    if (!primaryBill?.startPeriod || !primaryBill?.endPeriod) return null;
    const start = new Date(primaryBill.startPeriod);
    const end = new Date(primaryBill.endPeriod);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    return `${start.toLocaleDateString("fr-FR")} → ${end.toLocaleDateString(
      "fr-FR",
    )}`;
  }, [primaryBill?.endPeriod, primaryBill?.startPeriod]);
  const delta = useMemo(() => {
    if (!isWrongAmount) return null;
    if (saleAmount == null || billsTotal == null) return null;
    return billsTotal - saleAmount;
  }, [billsTotal, isWrongAmount, saleAmount]);

  return (
    <GenericModal
      isOpen={isOpen}
      title={title}
      onClose={close}
      className="flashscan-manual-resolution-modal"
    >
      {target ? (
        <div className="flashscan-manual-resolution-modal-content">
          {isWrongAmount && comparison ? (
            <div className="flashscan-manual-resolution-modal-compare">
              <div className="flashscan-manual-resolution-modal-card">
                <div className="flashscan-manual-resolution-modal-card-header">
                  {t("pages.flashscan.manualResolution.saleCardTitle")}
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.id")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {comparison.sale.providerId ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.name")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {comparison.sale.name ?? comparison.sale.providerId ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.client")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {comparison.sale.accountName ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.date")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {saleDateLabel ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.amount")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value flashscan-manual-resolution-modal-card-value--danger">
                    {saleAmount != null
                      ? `${saleAmount.toLocaleString("fr-FR")} €`
                      : "-"}
                  </div>
                </div>
              </div>

              <div className="flashscan-manual-resolution-modal-card">
                <div className="flashscan-manual-resolution-modal-card-header">
                  {t("pages.flashscan.manualResolution.billCardTitle")}
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.id")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {primaryBill?.providerId ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.label")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {primaryBill?.soldItemsDescription ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.client")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {primaryBill?.accountName ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.date")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {billPeriodLabel ?? "-"}
                  </div>
                </div>
                <div className="flashscan-manual-resolution-modal-card-row">
                  <div className="flashscan-manual-resolution-modal-card-label">
                    {t("pages.flashscan.manualResolution.fields.amount")}
                  </div>
                  <div className="flashscan-manual-resolution-modal-card-value">
                    {billsTotal != null
                      ? `${billsTotal.toLocaleString("fr-FR")} €`
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flashscan-manual-resolution-modal-summary">
              <div className="flashscan-manual-resolution-modal-summary-title">
                {target.title}
              </div>
              <div className="flashscan-manual-resolution-modal-summary-subtitle">
                {t("pages.flashscan.manualResolution.sourceId", {
                  id: target.providerId,
                })}
              </div>
            </div>
          )}

          {isWrongAmount && delta != null ? (
            <div className="flashscan-manual-resolution-modal-delta">
              <div className="flashscan-manual-resolution-modal-delta-title">
                {t("pages.flashscan.manualResolution.deltaTitle")}
              </div>
              <div className="flashscan-manual-resolution-modal-delta-value">
                {delta.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                €
              </div>
            </div>
          ) : null}

          <div className="flashscan-manual-resolution-modal-comment">
            <div className="flashscan-manual-resolution-modal-comment-label">
              {t("pages.flashscan.manualResolution.commentLabel")}
            </div>
            <textarea
              className="flashscan-manual-resolution-modal-comment-input"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t(
                "pages.flashscan.manualResolution.commentPlaceholder",
              )}
            />
          </div>

          <div className="flashscan-manual-resolution-modal-footer">
            <div className="flashscan-manual-resolution-modal-footer-right">
              <GenericButton
                label={t("pages.flashscan.manualResolution.actions.ignore")}
                onClick={() => void apply("IGNORE")}
                theme="secondary"
                disabled={isSubmitting}
              />
              <GenericButton
                label={t("pages.flashscan.manualResolution.actions.validate")}
                onClick={() => void apply("VALIDATE_MANUALLY")}
                theme="primary"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      ) : null}
    </GenericModal>
  );
};

